import { Callout, Chip, ChipGroup, SelectableCardGroup, Spinner, Text } from "@/components/ui"
import { useScheduleSlots } from "@/hooks/use-schedule-slots"
import { useStorefrontScheduling } from "@/hooks/use-storefront-scheduling"
import { nameBlockingLine } from "@/lib/scheduling/cart-lines"
import type { ScheduleSlotsAddress } from "@/lib/scheduling/fetch-schedule-slots"
import { formatArrivalChoice, formatDayChip } from "@/lib/scheduling/format"
import {
  blockingLinesForDate,
  formatBlockingBanner,
  offeredDays,
  resolveSelectedDay,
  type ScheduleSlot,
} from "@/lib/scheduling/schedule-slots"
import type { ScheduledPaymentPolicy } from "@/lib/scheduling/types"
import type { ServiceType } from "@/types"
import { useEffect } from "react"
import { SlotOption, SlotPickerSection } from "./SlotPickerSection"

type FutureWindowsSlotPickerProps = {
  serviceType: ServiceType
  /** DELIVERY only; null until the customer picks one, which gates the grid. */
  address: ScheduleSlotsAddress | null
  selectedDate: string | null
  selected: ScheduleSlot | null
  onSelectDate: (date: string) => void
  onSelectSlot: (slot: ScheduleSlot, date: string) => void
  onPaymentPolicy: (payment: ScheduledPaymentPolicy) => void
}

const EMPTY_REASONS: Record<string, string> = {
  STORE_CLOSED: "A loja não abre nos próximos dias deste horizonte.",
  NO_INTERSECTION: "Nenhum dia atende a todos os itens da sacola.",
  SCHEDULING_DISABLED: "Este canal não aceita agendamento no momento.",
  OUTSIDE_HORIZON: "Não há dias disponíveis dentro do horizonte de agendamento.",
  NO_ETA: "Não conseguimos calcular o tempo de entrega para este endereço.",
}

/**
 * The full model's picker: a day chip per day of the horizon that actually has
 * a slot, then the ARRIVAL times of the chosen day (D3). Nothing is computed
 * here — the endpoint is the authority, and the checkout revalidates.
 */
export function FutureWindowsSlotPicker({
  serviceType,
  address,
  selectedDate,
  selected,
  onSelectDate,
  onSelectSlot,
  onPaymentPolicy,
}: FutureWindowsSlotPickerProps) {
  const { timezone } = useStorefrontScheduling()
  const { known, loading, slots, failed, lines } = useScheduleSlots({
    enabled: true,
    serviceType,
    address,
  })

  const days = offeredDays(slots)
  const day = resolveSelectedDay(slots, selectedDate)
  const zone = slots?.timezone || timezone || undefined

  useEffect(() => {
    if (slots) onPaymentPolicy(slots.payment)
  }, [slots, onPaymentPolicy])

  useEffect(() => {
    if (day && day.date !== selectedDate) onSelectDate(day.date)
  }, [day, selectedDate, onSelectDate])

  if (serviceType === "DELIVERY" && !address) {
    return (
      <SlotPickerSection subtitle="Escolha o endereço de entrega primeiro — os horários dependem do tempo até você." />
    )
  }

  if (loading || !known) {
    return (
      <SlotPickerSection>
        <Text role="status" size="base" tone="muted" className="flex items-center gap-2">
          <Spinner size="sm" tone="primary" />
          Carregando horários...
        </Text>
      </SlotPickerSection>
    )
  }

  if (failed) {
    return (
      <SlotPickerSection>
        <Callout tone="error" title="Não conseguimos carregar os horários">
          Tente novamente em instantes.
        </Callout>
      </SlotPickerSection>
    )
  }

  const blockingBanner = formatBlockingBanner(
    blockingLinesForDate(slots, day?.date ?? slots?.days[0]?.date ?? null),
    (line) => nameBlockingLine(lines, line),
  )

  return (
    <SlotPickerSection>
      {blockingBanner ? (
        <Callout tone="warning" className="mb-3">
          {blockingBanner}
        </Callout>
      ) : null}

      {days.length === 0 ? (
        <Callout tone="error" title="Nenhum horário disponível">
          {EMPTY_REASONS[slots?.emptyReason ?? ""] ??
            "Nenhum horário disponível para esta sacola no momento."}
        </Callout>
      ) : (
        <>
          <ChipGroup label="Dias disponíveis" value={day?.date ?? null} onValueChange={onSelectDate}>
            {days.map((candidate) => (
              <Chip key={candidate.date} value={candidate.date} size="sm">
                {formatDayChip(candidate.date, new Date(), zone)}
              </Chip>
            ))}
          </ChipGroup>

          <SelectableCardGroup
            label={serviceType === "DELIVERY" ? "Horários de chegada" : "Horários de retirada"}
            value={selected?.arrivalStart ?? null}
            onValueChange={(arrivalStart) => {
              const slot = day?.slots.find((candidate) => candidate.arrivalStart === arrivalStart)
              if (day && slot) onSelectSlot(slot, day.date)
            }}
            className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2"
          >
            {(day?.slots ?? []).map((slot) => (
              <SlotOption key={slot.arrivalStart} value={slot.arrivalStart}>
                {formatArrivalChoice(slot, serviceType, zone)}
              </SlotOption>
            ))}
          </SelectableCardGroup>

          <Text size="sm" tone="muted" className="mt-3">
            {serviceType === "DELIVERY"
              ? "O horário é a janela de chegada no seu endereço."
              : "O horário é quando o pedido estará pronto para retirada."}
          </Text>
        </>
      )}
    </SlotPickerSection>
  )
}
